/**
 * TemplatePicker Component
 * Visual template selector with search, grouping, and channel labels
 */

import { useState, useMemo } from 'react';
import { Search, FileText, Shield } from 'lucide-react';
import clsx from 'clsx';
import { useNotificationTemplates } from '../hooks/useNotificationTemplates';
import type { NotificationTemplate, Channel } from '../types';

// Channel color mapping
const CHANNEL_COLORS: Record<Channel, string> = {
  push: 'bg-indigo-500',
  push_silent: 'bg-gray-500',
  realtime: 'bg-green-500',
};

const CHANNEL_LABELS: Record<Channel, string> = {
  push: 'Push',
  push_silent: '静默',
  realtime: '应用内',
};

// Template category definitions
interface TemplateCategory {
  id: string;
  label: string;
  filter: (template: NotificationTemplate) => boolean;
}

const CATEGORIES: TemplateCategory[] = [
  {
    id: 'artifact',
    label: 'Artifact 通知',
    filter: t => ['lucky_charm_ready', 'soulmate_ready'].includes(t.name),
  },
  {
    id: 'system',
    label: '系统通知',
    filter: t => ['system_maintenance', 'system_announcement'].includes(t.name),
  },
  {
    id: 'marketing',
    label: '营销通知',
    filter: t => ['daily_fortune', 'feature_launch'].includes(t.name),
  },
  {
    id: 'silent',
    label: '静默推送',
    filter: t => t.channels.includes('push_silent'),
  },
  {
    id: 'workflow',
    label: '工作流通知',
    filter: t => t.name.startsWith('workflow_'),
  },
  {
    id: 'other',
    label: '其他',
    filter: () => true, // Catch-all for remaining templates
  },
];

interface TemplatePickerProps {
  value: string | null;
  onChange: (templateName: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export function TemplatePicker({ value, onChange, disabled, className }: TemplatePickerProps) {
  const [search, setSearch] = useState('');
  const { data: templates = [] as NotificationTemplate[], isLoading } = useNotificationTemplates();

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates;

    const searchLower = search.toLowerCase();
    return templates.filter(
      t =>
        t.display_name.toLowerCase().includes(searchLower) ||
        t.name.toLowerCase().includes(searchLower) ||
        t.description?.toLowerCase().includes(searchLower)
    );
  }, [templates, search]);

  // Group templates by category
  const groupedTemplates = useMemo(() => {
    const groups: Record<string, NotificationTemplate[]> = {};
    const categorized = new Set<string>();

    // First pass: assign to specific categories
    for (const category of CATEGORIES) {
      if (category.id === 'other') continue; // Skip catch-all

      groups[category.id] = filteredTemplates.filter(t => {
        if (categorized.has(t.name)) return false;
        if (category.filter(t)) {
          categorized.add(t.name);
          return true;
        }
        return false;
      });
    }

    // Second pass: assign remaining to "other"
    groups.other = filteredTemplates.filter(t => !categorized.has(t.name));

    return groups;
  }, [filteredTemplates]);

  const renderTemplateCard = (template: NotificationTemplate) => {
    const isSelected = value === template.name;

    return (
      <div
        key={template.name}
        className={clsx(
          'p-3 rounded-lg border-2 cursor-pointer transition-all',
          isSelected
            ? 'border-indigo-500 bg-indigo-50'
            : 'border-gray-200 bg-white hover:border-gray-300',
          disabled && 'opacity-60 cursor-not-allowed'
        )}
        onClick={() => !disabled && onChange(template.name)}
      >
        <div className="flex items-start gap-3">
          {/* Radio indicator */}
          <div className={clsx(
            'w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center',
            isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
          )}>
            {isSelected && (
              <div className="w-1.5 h-1.5 rounded-full bg-white" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-900 truncate">
                {template.display_name}
              </span>
              {template.is_system && (
                <span title="系统模板"><Shield className="w-3.5 h-3.5 text-gray-400 shrink-0" /></span>
              )}
            </div>

            {/* Description */}
            {template.description && (
              <p className="text-xs text-gray-500 line-clamp-1 mb-2">
                {template.description}
              </p>
            )}

            {/* Channel badges */}
            <div className="flex flex-wrap gap-1">
              {template.channels.map(channel => (
                <span
                  key={channel}
                  className={clsx(
                    'inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded text-white',
                    CHANNEL_COLORS[channel]
                  )}
                >
                  {CHANNEL_LABELS[channel]}
                </span>
              ))}
            </div>
          </div>

          {/* Content preview */}
          <div className="text-right text-xs text-gray-500 shrink-0 max-w-[120px]">
            {template.content.title && (
              <p className="font-medium text-gray-700 truncate">{template.content.title}</p>
            )}
            <p className="truncate">{template.content.body?.substring(0, 20)}...</p>
          </div>
        </div>
      </div>
    );
  };

  const renderCategory = (categoryId: string) => {
    const category = CATEGORIES.find(c => c.id === categoryId);
    const categoryTemplates = groupedTemplates[categoryId] || [];

    if (categoryTemplates.length === 0) return null;

    return (
      <div key={categoryId} className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          {category?.label || categoryId}
        </h4>
        <div className="space-y-2">
          {categoryTemplates.map(renderTemplateCard)}
        </div>
      </div>
    );
  };

  return (
    <div className={clsx('space-y-4', className)}>
      <label className="block text-sm font-medium text-gray-700">
        选择通知模板
      </label>

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="搜索模板..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          disabled={disabled}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* No template option */}
      <div
        className={clsx(
          'p-3 rounded-lg border-2 cursor-pointer transition-all',
          value === null
            ? 'border-indigo-500 bg-indigo-50'
            : 'border-gray-200 bg-white hover:border-gray-300',
          disabled && 'opacity-60 cursor-not-allowed'
        )}
        onClick={() => !disabled && onChange(null)}
      >
        <div className="flex items-center gap-3">
          <div className={clsx(
            'w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center',
            value === null ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
          )}>
            {value === null && (
              <div className="w-1.5 h-1.5 rounded-full bg-white" />
            )}
          </div>
          <FileText className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-700">不使用模板（自定义内容）</span>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="py-8 text-center text-gray-500">
          <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-sm">加载模板中...</p>
        </div>
      )}

      {/* Template categories */}
      {!isLoading && (
        <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
          {CATEGORIES.map(cat => renderCategory(cat.id))}
        </div>
      )}

      {/* No results */}
      {!isLoading && filteredTemplates.length === 0 && search && (
        <div className="py-8 text-center text-gray-500">
          <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">未找到匹配的模板</p>
        </div>
      )}
    </div>
  );
}

// Compact dropdown version
interface TemplatePickerCompactProps {
  value: string | null;
  onChange: (templateName: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export function TemplatePickerCompact({ value, onChange, disabled, className }: TemplatePickerCompactProps) {
  const { data: templates = [] as NotificationTemplate[], isLoading } = useNotificationTemplates();

  return (
    <div className={clsx('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700">
        通知模板
      </label>

      <select
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        disabled={disabled || isLoading}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <option value="">不使用模板（自定义内容）</option>
        {templates.map(template => (
          <option key={template.name} value={template.name}>
            {template.display_name} ({template.name})
          </option>
        ))}
      </select>
    </div>
  );
}
